/**
 * Super Admin tabs — rendered ONLY when the logged-in user has role === 'super_admin'.
 * Provides:
 *   - PermissionsMatrix : grid editor for admin tab_permissions
 *   - SystemHealthTab   : collection counts + storage + last-24h activity
 *   - BrandingTab       : platform branding controls
 *   - BackupsTab        : create + list + download DB snapshots
 *
 * Uses the `api(method, path, body)` function-style wrapper from PanelAuthContext.
 * Paths are relative to /api/panel — the wrapper prefixes that.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Shield, Database, Palette, HardDrive, Loader2, Download, Plus, RefreshCw,
  CheckCircle2, ArrowUp, ArrowDown, Search, Save, AlertCircle, UserPlus, Trash2,
  X, Lock, KeyRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(2)} ${u[i]}`;
};
const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function Section({ title, subtitle, actions, children }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );
}

// ============================================================
// (1) PERMISSIONS MATRIX
// ============================================================
export function PermissionsMatrixTab({ api }) {
  const [matrix, setMatrix] = useState({ catalog: [], admins: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);          // admin object or null
  const [resetPwFor, setResetPwFor] = useState(null);    // admin object or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('GET', '/super-admin/rbac-matrix');
      setMatrix(r.data || { catalog: [], admins: [] });
    } catch { /* */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const grouped = (matrix.catalog || []).reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const togglePerm = async (admin, permKey) => {
    const has = (admin.permissions || []).includes(permKey) || (admin.permissions || []).includes('*');
    const next = has
      ? (admin.permissions || []).filter(p => p !== permKey && p !== '*')
      : [...(admin.permissions || []).filter(p => p !== '*'), permKey];
    setSaving(s => ({ ...s, [admin.id]: true }));
    try {
      await api('POST', `/super-admin/admins/${admin.id}/permissions`, { permissions: next });
      setMatrix(m => ({
        ...m,
        admins: m.admins.map(a => a.id === admin.id ? { ...a, permissions: next } : a),
      }));
      setMsg(`Updated ${admin.name}`); setTimeout(() => setMsg(''), 2000);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setSaving(s => ({ ...s, [admin.id]: false })); }
  };

  const grantAll = async (admin) => {
    if (!window.confirm(`Grant ALL permissions to ${admin.name}?`)) return;
    setSaving(s => ({ ...s, [admin.id]: true }));
    try {
      await api('POST', `/super-admin/admins/${admin.id}/permissions`, { permissions: ['*'] });
      setMatrix(m => ({ ...m, admins: m.admins.map(a => a.id === admin.id ? { ...a, permissions: ['*'] } : a) }));
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setSaving(s => ({ ...s, [admin.id]: false })); }
  };

  const revokeAll = async (admin) => {
    if (!window.confirm(`Revoke ALL permissions from ${admin.name}?`)) return;
    setSaving(s => ({ ...s, [admin.id]: true }));
    try {
      await api('POST', `/super-admin/admins/${admin.id}/permissions`, { permissions: [] });
      setMatrix(m => ({ ...m, admins: m.admins.map(a => a.id === admin.id ? { ...a, permissions: [] } : a) }));
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setSaving(s => ({ ...s, [admin.id]: false })); }
  };

  const promote = async (admin) => {
    if (!window.confirm(`Promote ${admin.name} to SUPER ADMIN? They will get full unrestricted access.`)) return;
    try {
      await api('POST', `/super-admin/admins/${admin.id}/promote`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const toggleSuspend = async (admin) => {
    const next = admin.status === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${next === 'active' ? 'Reactivate' : 'Suspend'} ${admin.name}?`)) return;
    try {
      await api('PATCH', `/super-admin/admins/${admin.id}`, { status: next });
      setMsg(`${admin.name} → ${next}`); setTimeout(() => setMsg(''), 2000);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const removeAdmin = async (admin) => {
    if (!window.confirm(`Permanently DELETE admin user "${admin.name}"? This cannot be undone.`)) return;
    try {
      await api('DELETE', `/super-admin/admins/${admin.id}`);
      setMsg(`Deleted ${admin.name}`); setTimeout(() => setMsg(''), 2000);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="permissions-matrix-tab">
      <Section
        title="Roles & Permissions"
        subtitle="Create admin users and control exactly what each can see and do. Super Admin always has full access."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} data-testid="rbac-refresh">
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="rbac-create-admin">
              <UserPlus size={14} className="mr-1.5" /> Create Admin
            </Button>
          </>
        }
      >
        {msg && (
          <div className="mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-center gap-1.5">
            <CheckCircle2 size={14} /> {msg}
          </div>
        )}
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {!loading && (matrix.admins || []).length === 0 && (
          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
            <Shield size={36} className="mx-auto text-slate-300 mb-3" />
            <div className="font-semibold text-slate-700">No admin users yet</div>
            <div className="text-sm text-slate-500 mt-1 mb-4">Use the button below to create your first admin user.</div>
            <Button onClick={() => setShowCreate(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="rbac-create-admin-empty">
              <UserPlus size={14} className="mr-1.5" /> Create First Admin
            </Button>
          </div>
        )}
        {(matrix.admins || []).map(a => {
          const allGranted = (a.permissions || []).includes('*');
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-2xl p-4 mb-3" data-testid={`rbac-admin-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-bold text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.email} · {a.mobile} · {a.designation || 'Admin'}</div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${a.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{a.status}</span>
                  <Button size="sm" variant="outline" onClick={() => grantAll(a)} disabled={saving[a.id]} data-testid={`rbac-grant-all-${a.id}`}>Grant all</Button>
                  <Button size="sm" variant="outline" onClick={() => revokeAll(a)} disabled={saving[a.id]} className="text-red-600">Revoke all</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`rbac-edit-${a.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setResetPwFor(a)} data-testid={`rbac-reset-pw-${a.id}`}><KeyRound size={12} className="mr-1" /> Reset PW</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleSuspend(a)} className={a.status === 'active' ? 'text-amber-700' : 'text-emerald-700'} data-testid={`rbac-suspend-${a.id}`}>
                    <Lock size={12} className="mr-1" /> {a.status === 'active' ? 'Suspend' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => promote(a)} className="text-purple-700">
                    <ArrowUp size={12} className="mr-1" /> Promote
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeAdmin(a)} className="text-rose-700" data-testid={`rbac-delete-${a.id}`}>
                    <Trash2 size={12} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>

              {allGranted && (
                <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2 mb-2 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={13} /> Wildcard granted — full access to every tab/action.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(grouped).map(([group, perms]) => (
                  <div key={group} className="bg-slate-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{group}</div>
                    <div className="space-y-1.5">
                      {perms.map(p => {
                        const has = allGranted || (a.permissions || []).includes(p.key);
                        return (
                          <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-2 py-1">
                            <input
                              type="checkbox"
                              checked={has}
                              disabled={saving[a.id] || allGranted}
                              onChange={() => togglePerm(a, p.key)}
                              data-testid={`rbac-check-${a.id}-${p.key}`}
                            />
                            <span className={`text-xs ${has ? 'text-slate-900 font-medium' : 'text-slate-500'}`}>{p.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </Section>

      <AdminFormModal
        open={showCreate || !!editing}
        api={api}
        admin={editing}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSaved={(savedAdmin) => {
          setShowCreate(false); setEditing(null);
          setMsg(editing ? `Updated ${savedAdmin?.name || 'admin'}` : `Created ${savedAdmin?.name || 'admin'}`);
          setTimeout(() => setMsg(''), 2500);
          load();
        }}
      />
      <ResetPasswordModal
        open={!!resetPwFor}
        api={api}
        admin={resetPwFor}
        onClose={() => setResetPwFor(null)}
        onSaved={() => {
          setResetPwFor(null);
          setMsg('Password rotated');
          setTimeout(() => setMsg(''), 2500);
        }}
      />
    </div>
  );
}

// ----- Admin Create / Edit Modal -----
function AdminFormModal({ open, api, admin, onClose, onSaved }) {
  const isEdit = !!admin;
  const [form, setForm] = useState({ name: '', email: '', mobile: '', designation: 'Admin', password: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setErr('');
    if (admin) {
      setForm({
        name: admin.name || '',
        email: admin.email || '',
        mobile: admin.mobile || '',
        designation: admin.designation || 'Admin',
        password: '',
      });
    } else {
      setForm({ name: '', email: '', mobile: '', designation: 'Admin', password: '' });
    }
  }, [open, admin]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setErr('');
    if (!form.name.trim()) return setErr('Name is required');
    if (!form.email.trim() || !form.email.includes('@')) return setErr('Valid email is required');
    if (!/^\d{10}$/.test(form.mobile)) return setErr('Mobile must be 10 digits');
    if (!isEdit && form.password.length < 8) return setErr('Password must be at least 8 characters');
    setBusy(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name, email: form.email, mobile: form.mobile,
          designation: form.designation,
        };
        if (form.password) payload.password = form.password;
        const r = await api('PATCH', `/super-admin/admins/${admin.id}`, payload);
        onSaved?.(r.data);
      } else {
        const r = await api('POST', '/super-admin/admins', form);
        onSaved?.(r.data);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-form-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-emerald-50">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-emerald-700" />
            <h3 className="font-bold text-slate-900">{isEdit ? 'Edit Admin' : 'Create Admin'}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Full Name *</Label>
            <Input value={form.name} onChange={update('name')} data-testid="admin-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={update('email')} data-testid="admin-form-email" />
            </div>
            <div>
              <Label className="text-xs">Mobile (10-digit) *</Label>
              <Input value={form.mobile} maxLength={10} onChange={update('mobile')} data-testid="admin-form-mobile" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Designation</Label>
            <Input value={form.designation} onChange={update('designation')} placeholder="e.g. Operations Lead" data-testid="admin-form-designation" />
          </div>
          <div>
            <Label className="text-xs">{isEdit ? 'New Password (leave blank to keep)' : 'Password (min 8 chars) *'}</Label>
            <Input type="password" value={form.password} onChange={update('password')} placeholder={isEdit ? 'Leave blank to keep' : 'min 8 chars'} data-testid="admin-form-password" />
          </div>
          {!isEdit && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
              By default this admin will receive the recommended permission set (Overview, Schedule, Bookings, Work Orders, Approvals, Warranties, Customers, Leads, Tickets, Quotations, Invoices, Payments, Reviews, Audit). You can fine-tune from the matrix below after creation.
            </div>
          )}
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-2" data-testid="admin-form-error"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="admin-form-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</> : (isEdit ? 'Save Changes' : 'Create Admin')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----- Reset Password Modal -----
function ResetPasswordModal({ open, api, admin, onClose, onSaved }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setPassword(''); setErr(''); } }, [open]);

  if (!open || !admin) return null;

  const submit = async () => {
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setBusy(true); setErr('');
    try {
      await api('PATCH', `/super-admin/admins/${admin.id}`, { password });
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-amber-700" />
            <h3 className="font-bold text-slate-900">Reset Password</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Rotating password for <b>{admin.name}</b> ({admin.email}). Share the new password via a secure channel.
          </div>
          <div>
            <Label className="text-xs">New Password (min 8 chars)</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-reset-pw-input" />
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-2"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="admin-reset-pw-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</> : 'Update Password'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// (2) SYSTEM HEALTH
// ============================================================
export function SystemHealthTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('GET', '/super-admin/system-health'); setData(r.data); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (!data) return null;

  const total24h = (data.last_24h?.customer_signups || 0) + (data.last_24h?.bookings_created || 0) + (data.last_24h?.panel_logins || 0);

  return (
    <div data-testid="system-health-tab">
      <Section title="System Health" subtitle="Live database metrics · refreshes every 30s" actions={
        <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      }>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Data Size" value={fmtBytes(data.storage?.data_bytes)} Icon={Database} color="emerald" />
          <StatCard label="Index Size" value={fmtBytes(data.storage?.index_bytes)} Icon={HardDrive} color="blue" />
          <StatCard label="Activity 24h" value={total24h} Icon={ArrowUp} color="purple" />
          <StatCard label="Last check" value={fmtDate(data.uptime_check_at)} Icon={CheckCircle2} color="slate" small />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
          <h3 className="font-semibold text-slate-900 mb-3">Last 24h activity</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Mini label="Customer signups" value={data.last_24h?.customer_signups || 0} />
            <Mini label="Bookings created" value={data.last_24h?.bookings_created || 0} />
            <Mini label="Panel logins" value={data.last_24h?.panel_logins || 0} />
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Collection sizes</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(data.counts || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex justify-between items-center px-3 py-2 bg-slate-50 rounded-lg text-sm">
                <span className="text-slate-600 truncate">{k}</span>
                <span className={`font-mono font-semibold ${v < 0 ? 'text-red-500' : 'text-slate-900'}`}>{v < 0 ? 'err' : v.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

function StatCard({ label, value, Icon, color = 'emerald', small }) {
  const palette = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700',
    slate: 'bg-slate-50 text-slate-700',
  }[color];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <div className={`p-1.5 rounded-lg ${palette}`}><Icon size={14} /></div>
      </div>
      <div className={`font-bold text-slate-900 ${small ? 'text-sm' : 'text-2xl'}`}>{value}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl py-3 px-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

// ============================================================
// (3) BRANDING
// ============================================================
export function BrandingTab({ api }) {
  const [form, setForm] = useState({
    business_name: '', tagline: '', primary_color: '#047857', accent_color: '#10b981',
    logo_url: '', favicon_url: '', support_email: '', support_phone: '', address: '',
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api('GET', '/super-admin/branding')
      .then(r => { if (alive) setForm(f => ({ ...f, ...(r.data || {}) })); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api]);

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      await api('PUT', '/super-admin/branding', form);
      setMsg('Branding updated · live changes apply on next page load');
      setTimeout(() => setMsg(''), 4000);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="branding-tab">
      <Section title="Branding" subtitle="Visible to customers via the public /api/branding endpoint">
        {loading ? <Loader2 size={16} className="animate-spin text-slate-400" /> : (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Business Name</Label><Input value={form.business_name || ''} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="EntoRid" data-testid="brand-name" /></div>
              <div><Label>Tagline</Label><Input value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="India's Most Trusted Pest Control" /></div>
              <div>
                <Label>Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.primary_color || '#047857'} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-14 border border-slate-300 rounded cursor-pointer" data-testid="brand-primary-color" />
                  <Input value={form.primary_color || ''} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="#047857" className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label>Accent Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.accent_color || '#10b981'} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 border border-slate-300 rounded cursor-pointer" />
                  <Input value={form.accent_color || ''} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} placeholder="#10b981" className="font-mono text-xs" />
                </div>
              </div>
              <div><Label>Logo URL</Label><Input value={form.logo_url || ''} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" /></div>
              <div><Label>Favicon URL</Label><Input value={form.favicon_url || ''} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })} placeholder="https://…/favicon.ico" /></div>
              <div><Label>Support Email</Label><Input value={form.support_email || ''} onChange={(e) => setForm({ ...form, support_email: e.target.value })} placeholder="support@entorid.com" /></div>
              <div><Label>Support Phone</Label><Input value={form.support_phone || ''} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} placeholder="+91-7819988010" /></div>
              <div className="md:col-span-2"><Label>Address</Label><Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Office address shown in footer" /></div>
            </div>

            {/* Live preview */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              <div className="text-xs text-slate-500 mb-2 font-semibold uppercase">Preview</div>
              <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${form.primary_color || '#047857'}, ${form.accent_color || '#10b981'})` }}>
                <div className="font-bold text-lg">{form.business_name || 'EntoRid'}</div>
                <div className="text-sm opacity-90">{form.tagline || 'India\'s Most Trusted Pest Control'}</div>
              </div>
            </div>

            {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> {msg}</div>}
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-center gap-1.5"><AlertCircle size={14} /> {err}</div>}

            <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="brand-save-btn">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save branding
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
}

// ============================================================
// (4) BACKUPS
// ============================================================
export function BackupsTab({ api }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('GET', '/super-admin/backups'); setBackups(r.data || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!window.confirm('Create a fresh backup snapshot now?')) return;
    setCreating(true);
    try {
      const r = await api('POST', '/super-admin/backup');
      setMsg(`Backup created: ${r.data.filename} (${r.data.total_documents.toLocaleString()} docs)`);
      setTimeout(() => setMsg(''), 5000);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setCreating(false); }
  };

  const download = (filename) => {
    const token = localStorage.getItem('entorid_panel_token') || '';
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/panel/super-admin/backups/${filename}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(blob => {
        const dl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl; a.download = filename; a.click();
        URL.revokeObjectURL(dl);
      })
      .catch(e => alert('Download failed: ' + e.message));
  };

  return (
    <div data-testid="backups-tab">
      <Section
        title="Database Backups"
        subtitle="Snapshot critical collections to a gzipped JSON file you can download for offline storage."
        actions={
          <Button onClick={create} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="backup-create-btn">
            {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
            Create backup now
          </Button>
        }
      >
        {msg && <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> {msg}</div>}
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {!loading && backups.length === 0 && (
          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
            <HardDrive size={36} className="mx-auto text-slate-300 mb-3" />
            <div className="font-semibold text-slate-700">No backups yet</div>
            <div className="text-sm text-slate-500 mt-1">Click &quot;Create backup now&quot; to capture a snapshot of all major collections.</div>
          </div>
        )}
        {backups.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            {backups.map(b => (
              <div key={b.filename} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`backup-${b.filename}`}>
                <div>
                  <div className="font-mono text-sm font-semibold text-slate-900">{b.filename}</div>
                  <div className="text-xs text-slate-500">{fmtBytes(b.size_bytes)} · {fmtDate(b.created_at)}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => download(b.filename)} data-testid={`backup-download-${b.filename}`}>
                  <Download size={13} className="mr-1.5" /> Download
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ============================================================
// (5) FULL AUDIT LOG (cross-user)
// ============================================================
export function FullAuditTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = `/super-admin/audit-log${actionFilter ? `?action=${encodeURIComponent(actionFilter)}` : ''}`;
      const r = await api('GET', path);
      setRows(r.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api, actionFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="full-audit-tab">
      <Section title="Full Audit Log" subtitle="Every privileged action across all admins · last 200 entries"
        actions={<Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>}
      >
        <div className="mb-3 flex gap-2 flex-wrap">
          {['', 'panel_login_success', 'panel_login_failed', 'rbac_update', 'db_backup', 'branding_update', 'employee_create', 'booking_update'].map(a => (
            <button key={a || 'all'}
              onClick={() => setActionFilter(a)}
              className={`px-3 py-1 text-xs font-semibold rounded-full ${actionFilter === a ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >{a || 'all'}</button>
          ))}
        </div>
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {!loading && rows.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Details</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">OK?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.user_name || '—'}</div>
                      <div className="text-[10px] text-slate-400 uppercase">{r.user_role || '—'}</div>
                    </td>
                    <td className="px-3 py-2"><code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">{r.action}</code></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.details || '—'}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-400 font-mono">{r.ip || '—'}</td>
                    <td className="px-3 py-2 text-center">{r.success ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertCircle size={14} className="text-red-500" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// EmailSettingsTab — switch provider (Resend / Gmail SMTP) + send test mail
// ════════════════════════════════════════════════════════════════════
export function EmailSettingsTab({ api }) {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState('resend');
  const [sender, setSender] = useState('');
  const [gmailUser, setGmailUser] = useState('');
  const [gmailPw, setGmailPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    const r = await api('GET', '/super-admin/email-settings');
    setData(r.data);
    setProvider(r.data?.provider || 'resend');
    setSender(r.data?.sender_email || '');
    setGmailUser(r.data?.gmail_user || '');
    setGmailPw(''); // never echo password back
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { provider, sender_email: sender, gmail_user: gmailUser };
      if (gmailPw.trim()) body.gmail_app_password = gmailPw.trim();
      const r = await api('POST', '/super-admin/email-settings', body);
      setMsg({ kind: 'ok', text: `Saved · provider=${r.data?.provider} · sender=${r.data?.sender_email}` });
      setGmailPw('');
      load();
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Save failed' });
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api('POST', '/super-admin/email-settings/test', { to: testTo });
      setMsg({ kind: 'ok', text: `Test email sent via ${r.data?.provider} · id=${r.data?.id} · to ${r.data?.delivered_to}. Check your inbox.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Test send failed — see backend logs' });
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;

  const usingResendSandbox = provider === 'resend' && (sender || '').includes('resend.dev');
  const gmailReady = provider === 'gmail_smtp' && gmailUser && (data.gmail_password_configured || gmailPw.trim());

  return (
    <div className="space-y-5" data-testid="email-settings-tab">
      {/* Provider selector */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2">📧 Email Delivery Provider</h3>
        <p className="text-xs text-slate-500 mb-4">Chooses which service actually sends OTPs, receipts and lead notifications.</p>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={() => setProvider('resend')}
            data-testid="provider-resend"
            className={`text-left rounded-xl border-2 p-4 transition ${provider === 'resend' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <div className="font-bold text-slate-900">Resend API</div>
            <div className="text-xs text-slate-600 mt-1">Best for high-volume. Needs a verified domain on resend.com to deliver to customers.</div>
            {provider === 'resend' && <div className="mt-2 text-[11px] font-bold text-emerald-700">● ACTIVE</div>}
          </button>

          <button
            type="button"
            onClick={() => setProvider('gmail_smtp')}
            data-testid="provider-gmail"
            className={`text-left rounded-xl border-2 p-4 transition ${provider === 'gmail_smtp' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <div className="font-bold text-slate-900">Gmail SMTP <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-1">RECOMMENDED</span></div>
            <div className="text-xs text-slate-600 mt-1">Sends from your Gmail account using an App Password. Delivers to ANY recipient — no DNS needed.</div>
            {provider === 'gmail_smtp' && <div className="mt-2 text-[11px] font-bold text-emerald-700">● ACTIVE</div>}
          </button>
        </div>

        {/* Resend sandbox warning */}
        {usingResendSandbox && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <div className="font-bold mb-1">⚠️ Resend sandbox sender — customers will NOT receive emails</div>
            With <code className="bg-amber-100 px-1 rounded">onboarding@resend.dev</code> Resend only delivers to the account owner ({data.admin_email}). Either:
            <ul className="list-disc ml-5 mt-1.5 space-y-0.5">
              <li>Switch to <b>Gmail SMTP</b> (above) — works in 5 minutes, no DNS</li>
              <li>OR verify <code>entorid.com</code> on <a className="underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a> and set sender to <code>noreply@entorid.com</code></li>
            </ul>
          </div>
        )}

        {/* Gmail SMTP setup help */}
        {provider === 'gmail_smtp' && (
          <div className="mb-4 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900">
            <div className="font-bold mb-1">🛠 One-time Gmail setup</div>
            <ol className="list-decimal ml-5 mt-1 space-y-0.5">
              <li>Enable 2-Step Verification on your Google account</li>
              <li>Open <a className="underline font-semibold" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a></li>
              <li>Create a new App Password (name it &quot;EntoRid&quot;) — copy the 16-character code</li>
              <li>Paste it in the &quot;App Password&quot; field below and click Save</li>
            </ol>
          </div>
        )}

        {/* Common: sender email */}
        <div className="mb-3">
          <Label className="text-xs font-semibold text-slate-700">Sender email address (&quot;From&quot;)</Label>
          <Input
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            placeholder={provider === 'gmail_smtp' ? 'official.entorid@gmail.com (defaults to Gmail user)' : 'noreply@entorid.com'}
            data-testid="email-sender-input"
            className="mt-1"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            {provider === 'gmail_smtp'
              ? 'Gmail may rewrite this to your Gmail user. Safest: leave equal to Gmail User below.'
              : 'Must be on a domain you have verified with Resend.'}
          </p>
        </div>

        {/* Gmail-only fields */}
        {provider === 'gmail_smtp' && (
          <>
            <div className="mb-3">
              <Label className="text-xs font-semibold text-slate-700">Gmail User (your @gmail.com address)</Label>
              <Input
                type="email"
                value={gmailUser}
                onChange={(e) => setGmailUser(e.target.value)}
                placeholder="official.entorid@gmail.com"
                data-testid="gmail-user-input"
                className="mt-1"
              />
            </div>
            <div className="mb-3">
              <Label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                <span>App Password (16 characters)</span>
                {data.gmail_password_configured && !gmailPw && (
                  <span className="text-[10px] text-emerald-700 font-bold">✓ saved · leave blank to keep</span>
                )}
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={gmailPw}
                  onChange={(e) => setGmailPw(e.target.value)}
                  placeholder={data.gmail_password_configured ? '••••••••••••••••' : 'xxxx xxxx xxxx xxxx'}
                  data-testid="gmail-app-password-input"
                  autoComplete="new-password"
                />
                <Button type="button" variant="outline" onClick={() => setShowPw((s) => !s)} data-testid="gmail-pw-toggle">
                  {showPw ? 'Hide' : 'Show'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Spaces are stripped automatically. Generate at <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a></p>
            </div>
          </>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={save} disabled={busy || (provider === 'gmail_smtp' && !gmailUser)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="email-sender-save">
            <Save size={14} className="mr-1.5" /> Save Settings
          </Button>
          <div className="text-[11px] text-slate-500">
            Currently active: <code className="bg-slate-100 px-1 rounded">{data.provider}</code> · <code className="bg-slate-100 px-1 rounded">{data.sender_email}</code>
            {data.provider === 'gmail_smtp' && data.gmail_user && <> · gmail: <code className="bg-slate-100 px-1 rounded">{data.gmail_user}</code></>}
          </div>
        </div>
      </div>

      {/* Test send */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h3 className="font-bold text-slate-900 mb-1">🔬 Send Test Email</h3>
        <p className="text-xs text-slate-500 mb-3">
          Pick a destination address (try a non-admin Gmail you own) to confirm delivery with your current configuration.
          {provider === 'gmail_smtp' && !gmailReady && <span className="text-amber-700"> · Save Gmail credentials first.</span>}
        </p>
        <div className="flex gap-2">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="customer@example.com" data-testid="email-test-to" />
          <Button onClick={test} disabled={busy || !testTo} variant="outline" data-testid="email-test-send">Send Test</Button>
        </div>
      </div>

      {msg && (
        <div className={`rounded-lg p-3 text-sm border ${msg.kind === 'ok' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`} data-testid="email-settings-msg">
          {msg.text}
        </div>
      )}
    </div>
  );
}
